import React, {
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  Pagination,
  Stack,
  Backdrop,
  useTheme,
  Tooltip,
  Menu,
  MenuList,
  Checkbox,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PAGE_SIZE } from "../../Services/common/const";
import Title from "../../Components/Title";
import ShortcutButton from "../../Components/ShortcutButton";
import { PreferencesContext } from "../../PreferencesContext";
import MissingConsumer from "../../Components/MissingConsumer";
import { ExportFilesFunction, numberFormat } from "../../shared/CommonFuctions";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import SplashComponent from "../../Navigation/splash";
import { AlertType } from "../../shared/Modal/AlertType";
import { EncryptDecryptService } from "../../Services/common/EncryptionService";
import { StyledMenu } from "../../Components/StyleMenus";
import { GetSaleSummaryByProduct } from "../../Services/SaleService";
import { format } from "date-fns";
import CustomDateRangePicker from "../../Components/CustomDateRange";
import { GetSaleSummaryByProductReport } from "../../Services/ReportService";
import MultiSelect from "../../Components/MultiSelect";
import { parametersForSalePage } from "../../StaticData/common";
import { GetProductList } from "../../Services/CategoryProductService";
import { GetBuyerList } from "../../Services/BuyerService";
import { GetWarehouseList } from "../../Services/WarehouseService";
import { GetProductCompanyList } from "../../Services/ProductCompanyService";
import CustomColumn from "../../Assets/custom-column.svg";

const COLUMN_STORAGE_KEY = "SALE_SUMMARY_BY_PRODUCT_COLUMNS";

const parameters = {
  consumerId: 25,
  branchId: 1,
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  lessThanValue: "",
};

type Order = "asc" | "desc" | "";

interface Data {
  productName: string;
  count: number;
  freeQuantity: number;
  quantity: number;
  totalQuantity: number;
  saleAmount: number;
  billAmount: number;
  productId: number;
  profitAmount: number;
  profitPercentage: number;
}

interface ApiResponse {
  data: {
    data: {
      saleSummaryProductListCollection: Data[];
      totalCount: number;
    };
  };
}

type ColumnKey =
  | "PRODUCT_NAME"
  | "INVOICE_COUNT"
  | "FREE_QUANTITY"
  | "QUANTITY"
  | "TOTAL_QUANTITY"
  | "SALE_AMOUNT"
  | "BILL_AMOUNT"
  | "PROFIT_AMOUNT"
  | "PROFIT_PERCENT";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SaleSummaryByProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [param, setParam] = useState(parameters);
  const [fromDate, setFromDate] = useState<any>(
    userContext.financialYearStartDate,
  );
  const [toDate, setToDate] = useState<any>(userContext.financialYearEndDate);
  const [order, setOrder] = useState<Order>("");
  const [orderBy, setOrderBy] = useState<keyof Data>("productName");
  const [resultData, setResultData] = useState<Data[]>([]);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const matches = useMediaQuery("(max-width:425px)");
  const edService = new EncryptDecryptService();
  const openExportType = Boolean(anchorEl2);
  const [ProductCollection, setProductCollection] = useState<any>([]);
  const [productSelectedOption, setProductSelectedOption] = useState<any>([]);
  const [selectedProductIdData, setSelectedProductIdData] = useState<any>([]);
  const [CustomerCollection, setCustomerCollection] = useState<any>([]);
  const [customersSelectedOption, setCustomerssSelectedOption] = useState<any>(
    [],
  );
  const [selectedCustomerIdData, setselectedCustomerIdData] = useState<any>([]);
  const [warehouseCollection, setWarehouseCollection] = useState<any>([]);
  const [warehouseSelectedOption, setWarehouseSelectedOption] = useState<any>(
    [],
  );
  const [selectedWarehouseIdData, setSelectedWarehouseIdData] = useState<any>(
    [],
  );
  const [productCompanyCollection, setProductCompanyCollection] = useState<any>(
    [],
  );
  const [companiesSelectedOption, setCompaniesSelectedOption] = useState<any>(
    [],
  );
  const [selectedProductCompanyIdData, setSelectedProductCompanyIdData] =
    useState<any>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const columnDefinitions = useMemo(
    () => [
      { key: "PRODUCT_NAME" as ColumnKey, label: "Product Name" },
      { key: "INVOICE_COUNT" as ColumnKey, label: "Invoice Count" },
      {
        key: "FREE_QUANTITY" as ColumnKey,
        label: "Free Quantity",
        requiresFreeQuantity: true,
      },
      { key: "QUANTITY" as ColumnKey, label: "Quantity" },
      {
        key: "TOTAL_QUANTITY" as ColumnKey,
        label: "Total Quantity",
        requiresFreeQuantity: true,
      },
      { key: "SALE_AMOUNT" as ColumnKey, label: "Sale A/C Amount" },
      { key: "BILL_AMOUNT" as ColumnKey, label: "Bill Amount" },
      { key: "PROFIT_AMOUNT" as ColumnKey, label: "Profit Amount (₹)" },
      { key: "PROFIT_PERCENT" as ColumnKey, label: "Profit (%)" },
    ],
    [],
  );

  const availableColumns = useMemo(
    () =>
      columnDefinitions.filter(
        (column) =>
          !column.requiresFreeQuantity || userContext.enableFreeQuantity,
      ),
    [columnDefinitions, userContext.enableFreeQuantity],
  );

  const buildDefaultColumnConfig = useCallback(() => {
    return availableColumns.reduce(
      (acc, column) => {
        acc[column.key] = true;
        return acc;
      },
      {} as Record<ColumnKey, boolean>,
    );
  }, [availableColumns]);

  const loadStoredColumnConfig = useCallback(() => {
    if (typeof window === "undefined") {
      return buildDefaultColumnConfig();
    }

    try {
      const rawValue = window.localStorage.getItem(COLUMN_STORAGE_KEY);
      if (!rawValue) {
        return buildDefaultColumnConfig();
      }
      const parsed = JSON.parse(rawValue);
      if (!parsed || typeof parsed !== "object") {
        return buildDefaultColumnConfig();
      }
      return availableColumns.reduce(
        (acc, column) => {
          const storedValue = parsed[column.key];
          acc[column.key] =
            typeof storedValue === "boolean" ? storedValue : true;
          return acc;
        },
        {} as Record<ColumnKey, boolean>,
      );
    } catch {
      return buildDefaultColumnConfig();
    }
  }, [availableColumns, buildDefaultColumnConfig]);

  const [columnConfig, setColumnConfig] = useState<Record<ColumnKey, boolean>>(
    () => loadStoredColumnConfig(),
  );
  const [columnConfigDraft, setColumnConfigDraft] = useState<
    Record<ColumnKey, boolean>
  >(() => loadStoredColumnConfig());

  const isSameConfig = useCallback(
    (left: Record<ColumnKey, boolean>, right: Record<ColumnKey, boolean>) =>
      availableColumns.every(
        (column) => left?.[column.key] === right?.[column.key],
      ),
    [availableColumns],
  );

  const persistColumnConfig = useCallback(
    (nextConfig: Record<ColumnKey, boolean>) => {
      if (typeof window === "undefined") {
        return;
      }
      window.localStorage.setItem(
        COLUMN_STORAGE_KEY,
        JSON.stringify(nextConfig),
      );
    },
    [],
  );

  useEffect(() => {
    const normalized = loadStoredColumnConfig();
    setColumnConfig((prev) =>
      isSameConfig(prev, normalized) ? prev : normalized,
    );
    setColumnConfigDraft((prev) =>
      isSameConfig(prev, normalized) ? prev : normalized,
    );
  }, [availableColumns, isSameConfig, loadStoredColumnConfig]);

  const visibleColumnCount = useMemo(() => {
    return availableColumns.filter((column) => columnConfig[column.key]).length;
  }, [availableColumns, columnConfig]);

  const pageInfo = useMemo(
    () => ({
      startIndex: (page - 1) * PAGE_SIZE + 1,
      endIndex: (page - 1) * PAGE_SIZE + resultData.length,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
    }),
    [page, resultData.length, totalCount],
  );

  const handleSearch = useCallback((val: string) => {
    setSearchValue(val);
    setPage(1);
    setParam((prev) => ({ ...prev, searchValue: val, pageIndex: 1 }));
  }, []);

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setParam((prev) => ({ ...prev, pageIndex: value }));
    },
    [],
  );

  const handleExport = useCallback(
    async (type: string) => {
      setAnchorEl2(null);
      setLoading(true);
      try {
        const params = new URLSearchParams({
          reportName: "SaleSummaryByProductReport",
          reportType: type,
          consumerId: userContext.consumerId.toString(),
          branchId: userContext.branchId.toString(),
          firmId: userContext.firmId.toString(),
          financialYear: userContext.financialYear.toString(),
          searchValue: param.searchValue || "",
          mode: "EXPORT",
          startDate: format(new Date(fromDate), "yyyy-MM-dd"),
          endDate: format(new Date(toDate), "yyyy-MM-dd"),
          isFreeQuantity: userContext.enableFreeQuantity.toString(),
          fileName: `SaleSummaryByProductReport${format(new Date(fromDate), "_yyyyMMdd")}${format(new Date(toDate), "_yyyyMMdd")}.${type === "pdf" ? "pdf" : "xls"}`,
          isForDownload: "1",
          ProductId: selectedProductIdData.toString(),
          CustomerId: selectedCustomerIdData.toString(),
          warehouseId: selectedWarehouseIdData.toString(),
          productCompanyIds: selectedProductCompanyIdData.toString(),
          showProductName: (!!columnConfig.PRODUCT_NAME).toString(),
          showInvoiceCount: (!!columnConfig.INVOICE_COUNT).toString(),
          showFreeQuantity: (
            userContext.enableFreeQuantity && !!columnConfig.FREE_QUANTITY
          ).toString(),
          showQuantity: (!!columnConfig.QUANTITY).toString(),
          showTotalQuantity: (
            userContext.enableFreeQuantity && !!columnConfig.TOTAL_QUANTITY
          ).toString(),
          showSaleAmount: (!!columnConfig.SALE_AMOUNT).toString(),
          showBillAmount: (!!columnConfig.BILL_AMOUNT).toString(),
          showProfitAmount: (!!columnConfig.PROFIT_AMOUNT).toString(),
          showProfitPercent: (!!columnConfig.PROFIT_PERCENT).toString(),
        });

        const result: any = await GetSaleSummaryByProductReport(params);
        if (result?.data?.data?.fileData) {
          ExportFilesFunction(
            params.get("fileName")!,
            type,
            result.data.data.fileData,
          );
        }
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      fromDate,
      toDate,
      param.searchValue,
      userContext,
      selectedProductIdData,
      selectedCustomerIdData,
      selectedWarehouseIdData,
      selectedProductCompanyIdData,
      columnConfig,
    ],
  );

  const handleMoveToSale = useCallback(
    async (productId: number) => {
      try {
        const encProductId = edService.encrypt(productId.toString());
        navigate({
          pathname: "/sale-by-product",
          search: `?q=${encodeURIComponent(encProductId || "")}`,
        });
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      }
    },
    [navigate, edService, userContext, updateUserContext],
  );

  const handleChangeCategory = (object: any) => {
    setProductSelectedOption(object);
    let tempProductIdData: any = [];
    object.forEach((x) => {
      if (x?.value === "*") {
        return;
      } else {
        let productId = x.productId;
        tempProductIdData.push(productId.toString());
      }
    });
    setSelectedProductIdData(tempProductIdData);
  };

  const handleChangeCategortwo = (object: any) => {
    setCustomerssSelectedOption(object);
    let tempCustomerIdData: any = [];
    object.forEach((x) => {
      if (x?.value === "*") {
        return;
      } else {
        let buyerIds = x.buyerId;
        tempCustomerIdData.push(buyerIds.toString());
      }
    });
    setselectedCustomerIdData(tempCustomerIdData);
  };

  const handleWarehouseId = (object: any) => {
    setWarehouseSelectedOption(object);
    let warehouseIdData: any = [];
    object.forEach((x) => {
      if (x?.value === "*") {
        return;
      } else {
        let warehouseIds = x.warehouseId;
        warehouseIdData.push(warehouseIds.toString());
      }
    });
    setSelectedWarehouseIdData(warehouseIdData);
  };

  const handleChangeCompany = (object: any[]) => {
    setCompaniesSelectedOption(object);
    let tempCompanyIdData: any = [];
    object.forEach((x) => {
      if (x?.value === "*") {
        return;
      } else {
        let companyId = x.productCompanyId;
        tempCompanyIdData.push(companyId.toString());
      }
    });
    setSelectedProductCompanyIdData(tempCompanyIdData);
  };

  async function GetProductLists() {
    let params = new URLSearchParams();
    params.append("consumerId", userContext.consumerId.toString());
    params.append("firmId", userContext.firmId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("pageIndex", parametersForSalePage.pageIndex.toString());
    params.append("pageSize", "5000");
    params.append("sortColumn", parametersForSalePage.sortColumn);
    params.append("sortDirection", parametersForSalePage.sortDirection);
    params.append("financialYear", userContext.financialYear.toString());

    try {
      let productCollection: any;
      const result: any = await GetProductList(params);
      if (result && result.data.data) {
        productCollection = result.data.data.productCollection;
        setProductCollection(productCollection);
      }
      return productCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function GetCustomerList() {
    let params = new URLSearchParams();
    params.append("consumerId", userContext.consumerId.toString());
    params.append("firmId", userContext.firmId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("pageIndex", parametersForSalePage.pageIndex.toString());
    params.append("pageSize", parametersForSalePage.pageSize.toString());
    params.append("sortColumn", parametersForSalePage.sortColumn);
    params.append("sortDirection", parametersForSalePage.sortDirection);
    params.append("financialYear", userContext.financialYear?.toString());

    try {
      const result: any = await GetBuyerList(params);
      if (result && result.data.data) {
        setCustomerCollection(result.data?.data?.buyerCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchWarehouseList() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId.toString());
      params.append("firmId", userContext.firmId.toString());
      params.append("branchId", userContext.branchId.toString());
      params.append("pageIndex", page.toString());
      params.append("pageSize", param.pageSize);
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "");

      const result: any = await GetWarehouseList(params);
      if (result && result.data.data) {
        setWarehouseCollection(result.data?.data?.warehouseCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchProductCompanyList() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId.toString());
      params.append("firmId", userContext.firmId.toString());
      params.append("branchId", userContext.branchId.toString());
      params.append("pageIndex", page.toString());
      params.append("pageSize", param.pageSize);
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN");

      const result: any = await GetProductCompanyList(params);
      if (result && result.data.data) {
        setProductCompanyCollection(
          result.data?.data?.productCompanyCollection,
        );
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const fetchSaleList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        consumerId: userContext.consumerId.toString(),
        firmId: userContext.firmId.toString(),
        branchId: userContext.branchId.toString(),
        pageIndex: page.toString(),
        pageSize: PAGE_SIZE.toString(),
        sortColumn: param.sortColumn || "",
        sortDirection: param.sortDirection || "",
        searchValue: param.searchValue || "",
        lessThanValue: param.lessThanValue || "",
        financialYear: userContext.financialYear,
        startDate: format(new Date(fromDate), "yyyy-MM-dd"),
        endDate: format(new Date(toDate), "yyyy-MM-dd"),
        productId: selectedProductIdData.toString(),
        customerId: selectedCustomerIdData.toString(),
        warehouseId: selectedWarehouseIdData.toString(),
        productCompanyIds: selectedProductCompanyIdData.toString(),
      });

      const result = (await GetSaleSummaryByProduct(params)) as ApiResponse;
      if (result?.data?.data?.saleSummaryProductListCollection) {
        const { saleSummaryProductListCollection, totalCount: dataCount } =
          result.data.data;
        setResultData(saleSummaryProductListCollection);
        setTotalCount(dataCount);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    param,
    fromDate,
    toDate,
    userContext,
    selectedProductIdData,
    selectedCustomerIdData,
    selectedWarehouseIdData,
    selectedProductCompanyIdData,
  ]);

  useEffect(() => {
    GetProductLists();
    GetCustomerList();
    fetchWarehouseList();
    fetchProductCompanyList();
  }, []);

  useEffect(() => {
    fetchSaleList();
  }, [fetchSaleList]);

  const handleCloseExportType = () => {
    setAnchorEl2(null);
  };

  const handleSettingsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setColumnConfigDraft(columnConfig);
  };

  const handleSettingsClose = () => {
    setMenuAnchorEl(null);
    setColumnConfigDraft(columnConfig);
  };

  const handleSettingsSave = () => {
    setColumnConfig(columnConfigDraft);
    persistColumnConfig(columnConfigDraft);
    setMenuAnchorEl(null);
  };

  const handleClickExportType = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl2(event.currentTarget);
  };

  const openSalesReportPopup = async (type: string) => {
    await handleExport(type);
  };

  const TableHeader = React.memo(() => (
    <TableHead className="table-header-color">
      <TableRow>
        {columnConfig.PRODUCT_NAME && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="left"
          >
            {"Product Name"}
          </TableCell>
        )}
        {columnConfig.INVOICE_COUNT && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="center"
          >
            {"Invoice Count"}
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.FREE_QUANTITY && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Free Quantity"}
          </TableCell>
        )}
        {columnConfig.QUANTITY && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Quantity"}
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.TOTAL_QUANTITY && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Total Quantity"}
          </TableCell>
        )}
        {columnConfig.SALE_AMOUNT && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Sale A/C Amount"}
          </TableCell>
        )}
        {columnConfig.BILL_AMOUNT && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Bill Amount"}
          </TableCell>
        )}
        {columnConfig.PROFIT_AMOUNT && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Profit Amount (₹)"}
          </TableCell>
        )}
        {columnConfig.PROFIT_PERCENT && (
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Profit (%)"}
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  ));

  const TableRowComponent = React.memo(
    ({
      item,
      handleMoveToSale,
    }: {
      item: Data;
      handleMoveToSale: (id: number) => void;
    }) => (
      <TableRow hover role="checkbox" tabIndex={-1}>
        {columnConfig.PRODUCT_NAME && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="left"
          >
            {item?.productName}
          </TableCell>
        )}
        {columnConfig.INVOICE_COUNT && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="center"
          >
            <Button
              disableRipple
              color="inherit"
              onClick={() => handleMoveToSale(item.productId)}
              className="manuButton-link"
              style={{ backgroundColor: "transparent" }}
            >
              <span className="text-link" style={{ fontSize: "15px" }}>
                {item?.count}
              </span>
            </Button>
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.FREE_QUANTITY && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.freeQuantity)}
          </TableCell>
        )}
        {columnConfig.QUANTITY && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.quantity)}
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.TOTAL_QUANTITY && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.totalQuantity)}
          </TableCell>
        )}
        {columnConfig.SALE_AMOUNT && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.saleAmount)}
          </TableCell>
        )}
        {columnConfig.BILL_AMOUNT && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.billAmount)}
          </TableCell>
        )}
        {columnConfig.PROFIT_AMOUNT && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.profitAmount)}
          </TableCell>
        )}
        {columnConfig.PROFIT_PERCENT && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
            }}
            align="right"
          >
            {numberFormat(item?.profitPercentage)}
          </TableCell>
        )}
      </TableRow>
    ),
  );

  const TableFooter = React.memo(({ resultData }: { resultData: Data[] }) => {
    const totals = useMemo(
      () => ({
        count: resultData.reduce((acc, curr) => acc + (curr?.count || 0), 0),
        freeQuantity: resultData.reduce(
          (acc, curr) => acc + (curr?.freeQuantity || 0),
          0,
        ),
        quantity: resultData.reduce(
          (acc, curr) => acc + (curr?.quantity || 0),
          0,
        ),
        totalQuantity: resultData.reduce(
          (acc, curr) => acc + (curr?.totalQuantity || 0),
          0,
        ),
        saleAmount: resultData.reduce(
          (acc, curr) => acc + (curr?.saleAmount || 0),
          0,
        ),
        billAmount: resultData.reduce(
          (acc, curr) => acc + (curr?.billAmount || 0),
          0,
        ),
        profitAmount: resultData.reduce(
          (acc, curr) => acc + (curr?.profitAmount || 0),
          0,
        ),
      }),
      [resultData],
    );

    return (
      <TableRow hover tabIndex={-1} key={"0"}>
        {columnConfig.PRODUCT_NAME && (
          <TableCell className="bold-600">Total</TableCell>
        )}
        {columnConfig.INVOICE_COUNT && (
          <TableCell align="center" className="bold-600">
            {totals.count}
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.FREE_QUANTITY && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.freeQuantity)}
          </TableCell>
        )}
        {columnConfig.QUANTITY && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.quantity)}
          </TableCell>
        )}
        {userContext.enableFreeQuantity && columnConfig.TOTAL_QUANTITY && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.totalQuantity)}
          </TableCell>
        )}
        {columnConfig.SALE_AMOUNT && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.saleAmount)}
          </TableCell>
        )}
        {columnConfig.BILL_AMOUNT && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.billAmount)}
          </TableCell>
        )}
        {columnConfig.PROFIT_AMOUNT && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.profitAmount)}
          </TableCell>
        )}
        {columnConfig.PROFIT_PERCENT && (
          <TableCell align="right" className="bold-600"></TableCell>
        )}
      </TableRow>
    );
  });

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <SplashComponent />
      </Backdrop>
      <Title pageTitle="Sales Summary By Product" />

      {userContext.consumerId > 0 ? (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <Grid
            className="title-button-responsive"
            item
            xs={6}
            md={3}
            sx={{
              display: "flex !important",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4 className="page-title">Sales Summary By Product</h4>

            <Grid
              item
              xs={6}
              md={3}
              marginRight={0}
              textAlign="end"
              paddingTop="0px"
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="medium"
              >
                <ShortcutButton label="ESC" />
                Back
              </Button>
            </Grid>
          </Grid>
          <Grid className="paper top-space">
            <Paper elevation={4} className="paper-container">
              <Grid
                container
                spacing={1}
                sx={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                }}
                className="search-space"
              >
                <Grid item xs={12} sm={12} md={4} lg={2} xl={2}>
                  <TextField
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch(searchValue);
                      }
                    }}
                    sx={{ paddingLeft: "0px !important", width: "100%" }}
                    className="button-responsive-stock"
                    placeholder="Search..."
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setSearchValue(e.target.value)}
                    value={searchValue}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleSearch(searchValue)}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <IconButton onClick={() => handleSearch("")}>
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={5}
                  xl={5}
                  sx={{ display: matches ? "" : "flex", gap: "10px" }}
                >
                  <Grid sx={{ flex: 1 }}>
                    <MultiSelect
                      key="BuyerId"
                      placeholderLabel="Products"
                      placeholder="Select Product.."
                      options={ProductCollection}
                      onChange={handleChangeCategory}
                      value={productSelectedOption}
                      isSelectAll={true}
                      menuPlacement={"bottom"}
                    />
                  </Grid>
                  <Grid sx={{ flex: 1, marginTop: matches ? "10px" : "" }}>
                    <MultiSelect
                      key="BuyerId"
                      placeholderLabel="Customers"
                      placeholder="Select Customer.."
                      options={CustomerCollection}
                      onChange={handleChangeCategortwo}
                      value={customersSelectedOption}
                      isSelectAll={true}
                      menuPlacement={"bottom"}
                    />
                  </Grid>
                  <Grid sx={{ flex: 1, marginTop: matches ? "10px" : "" }}>
                    <MultiSelect
                      key="WarehouseId"
                      placeholderLabel="Warehouses"
                      placeholder="Select Warehouse.."
                      options={warehouseCollection}
                      onChange={handleWarehouseId}
                      value={warehouseSelectedOption}
                      isSelectAll={true}
                      menuPlacement={"bottom"}
                    />
                  </Grid>
                  <Grid sx={{ flex: 1, marginTop: matches ? "10px" : "" }}>
                    <MultiSelect
                      key="ProductCompanyId"
                      placeholderLabel="Company"
                      placeholder="Select Company.."
                      options={productCompanyCollection}
                      onChange={handleChangeCompany}
                      value={companiesSelectedOption}
                      isSelectAll={true}
                      menuPlacement="bottom"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={5} xl={5}>
                  <Grid
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "stretch", md: "flex-end" },
                      alignItems: {
                        xs: "stretch",
                        md: "flex-start",
                        lg: "center",
                      },
                      gap: "5px",
                      flexWrap: "wrap",
                      "& > div, & > button, & > Box": {
                        width: { xs: "100%", md: "auto" },
                      },
                    }}
                  >
                    <CustomDateRangePicker
                      sx={{ marginTop: matches ? "10px !important" : "" }}
                      fromDate={fromDate}
                      setFromDate={setFromDate}
                      toDate={toDate}
                      setToDate={setToDate}
                      minDate={dayjs(userContext.financialYearStartDate)}
                      maxDate={dayjs(userContext.financialYearEndDate)}
                    />
                    <Button
                      className="button-responsive-stock"
                      aria-controls={
                        openExportType ? "demo-customized-menu" : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={openExportType ? "true" : undefined}
                      disabled={resultData?.length <= 0}
                      variant="outlined"
                      disableElevation
                      onClick={handleClickExportType}
                      startIcon={<DownloadIcon />}
                      endIcon={<KeyboardArrowDownSharp />}
                    >
                      Export
                    </Button>
                    <StyledMenu
                      id="demo-customized-menu"
                      MenuListProps={{
                        "aria-labelledby": "demo-customized-button",
                      }}
                      anchorEl={anchorEl2}
                      open={openExportType}
                      onClose={handleCloseExportType}
                    >
                      <MenuItem
                        disableRipple
                        onClick={() => openSalesReportPopup("pdf")}
                      >
                        PDF
                      </MenuItem>
                      <MenuItem
                        disableRipple
                        onClick={() => openSalesReportPopup("Excel")}
                      >
                        EXCEL
                      </MenuItem>
                    </StyledMenu>
                    <Box>
                      <Tooltip title="Customize Columns" placement="top">
                        <IconButton
                          aria-label="Settings"
                          onClick={handleSettingsOpen}
                        >
                          <img src={CustomColumn} alt="Settings" height={20} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        MenuListProps={{ disablePadding: true }}
                        onClose={handleSettingsClose}
                      >
                        <MenuList sx={{ padding: 0 }}>
                          <Box
                            sx={{
                              border: "1px solid #c7c5c5",
                              borderRadius: "4px",
                              display: "flex",
                              flexDirection: "column",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: "#dee6fa",
                                color: "#404d54",
                                padding: "10px",
                                fontWeight: "bold",
                              }}
                            >
                              Settings
                            </Box>
                            <Box
                              sx={{
                                maxHeight: "300px",
                                overflowY: "auto",
                              }}
                            >
                              {availableColumns.map((column) => (
                                <MenuItem
                                  key={column.key}
                                  sx={{
                                    alignItems: "start",
                                    paddingLeft: "5px",
                                  }}
                                  onClick={() =>
                                    setColumnConfigDraft((prev) => ({
                                      ...prev,
                                      [column.key]: !prev[column.key],
                                    }))
                                  }
                                >
                                  <Checkbox
                                    checked={columnConfigDraft[column.key]}
                                    sx={{
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px !important",
                                      fontWeight: "normal",
                                      paddingLeft: "8px",
                                    }}
                                  >
                                    {column.label}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                borderTop: "1px solid #ccc",
                                px: 2,
                                py: 1,
                                gap: "5px",
                              }}
                            >
                              <Button
                                size="small"
                                onClick={handleSettingsSave}
                                color="primary"
                                variant="contained"
                              >
                                Save
                              </Button>
                              <Button
                                onClick={handleSettingsClose}
                                size="small"
                              >
                                Close
                              </Button>
                            </Box>
                          </Box>
                        </MenuList>
                      </Menu>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <TableContainer>
                <Table aria-labelledby="tableTitle" size="medium">
                  <TableHeader />
                  {resultData?.length > 0 && (
                    <TableBody>
                      {resultData.map((item: Data, index: number) => (
                        <TableRowComponent
                          key={item.productId || index}
                          item={item}
                          handleMoveToSale={handleMoveToSale}
                        />
                      ))}
                      <TableFooter resultData={resultData} />
                    </TableBody>
                  )}
                  {(!resultData || resultData.length <= 0) && (
                    <TableBody>
                      <TableRow hover tabIndex={-1} key={"0"}>
                        <TableCell
                          colSpan={Math.max(visibleColumnCount, 1)}
                          align="center"
                        >
                          No record(s) found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
                {resultData?.length > 0 && (
                  <Grid
                    container
                    paddingTop={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Grid item xs={12} sm={6} className="pagination-label">
                      {pageInfo.startIndex} - {pageInfo.endIndex} of{" "}
                      {totalCount}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack
                        component="div"
                        alignItems={mobileScreen ? "center" : "end"}
                        justifyContent={"center"}
                      >
                        <Pagination
                          color="primary"
                          count={pageInfo.totalPages}
                          page={page}
                          onChange={handlePageChange}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                )}
              </TableContainer>
            </Paper>
          </Grid>
        </Box>
      ) : (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <MissingConsumer consumerId={userContext.consumerId} />
        </Box>
      )}
    </>
  );
};

export default React.memo(SaleSummaryByProduct);
